/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import * as React from 'react';
import CallToAction from './../../components/call-to-action';
import { IProductAttributesData } from './product-attributes.data';
import { IProductAttributesProps } from './product-attributes.props.autogenerated';

/**
 *
 * ProductAttributes component
 * @extends {React.PureComponent<IProductAttributesProps<IProductAttributesData>>}
 */
// @observer
class ProductAttributes extends React.PureComponent<
  IProductAttributesProps<IProductAttributesData>
  > {
  private _productAttributeFactSheetURL: string = 'FACT SHEET URL';
  private _productAttributeFactSheetHeader: string = 'DOWNLOAD FACTSHEET PDF';
  private _productAttributeTastingNotes: string = 'TASTING NOTES';
  private _productAttributeCookbookDescription: string = 'COOKBOOKDESCRIPTION';
  private _productAttributeCookbookDescriptionHeader: string = 'Description';
  private _productAttributeFoodPairings: string = 'FOOD PAIRINGS';
  private _productAttributeWineAnalysis: string = 'WINE ANALYSIS';
  private _productAttributeWineBlend: string = 'BLEND';
  private _productAttributeWineAging: string = 'AGING';
  private _productAttributeWineAlcohol: string = 'ALCOHOL';
  private _productAttributeWinePH: string = 'PH';
  private _productAttributeWineTA: string = 'TA';
  private _productAttributeWineF: string = 'F';

  public render(): JSX.Element | null {
    const content: IProductAttributesData = this.props.data;
    if (!(content && content.productSpecificationData && content.productSpecificationData.result)
    ) {
      return null;
    }

    return (
      <div className='product-attribute'>
        {this._renderProductAttributes(
          content.productSpecificationData.result,
          this._productAttributeFactSheetURL
        )}
        {this._renderProductAttributes(
          content.productSpecificationData.result,
          this._productAttributeTastingNotes
        )}
        {this._renderProductAttributes(
          content.productSpecificationData.result,
          this._productAttributeCookbookDescription
        )}
        {this._renderProductAttributes(
          content.productSpecificationData.result,
          this._productAttributeFoodPairings
        )}
        {this._renderProductAttributes(
          content.productSpecificationData.result,
          this._productAttributeWineAnalysis
        )}
      </div>
    );
  }

  private _renderProductAttributes(
    product: AttributeValue[],
    filter: string
  ): JSX.Element | null {
    if (product && product.length) {
      const locale = get(this.props, 'context.request.locale', 'en-US');
      if (
        this._compareStrings(filter, this._productAttributeFactSheetURL, true, locale)
      ) {
        const productattribute = product.filter(
          attribute => attribute.Name!.toUpperCase() === filter.toUpperCase()
        );
        if (productattribute && productattribute.length && productattribute[0].TextValue) {
          return (
            <div className='product-attribute-download'>
              <i className='far fa-arrow-alt-circle-down product-attribute-download-icon' />
              <CallToAction
                className='product-attribute-download-cta'
                title={this._productAttributeFactSheetHeader}
                color='link'
                href={productattribute[0].TextValue}
                layout='noGlyph'
              />
            </div>
          );
        }
      } else if (this._compareStrings(filter, this._productAttributeWineAnalysis, true, locale)) {
        if (this._renderWineAttributes(product, this._productAttributeWineBlend)
          || this._renderWineAttributes(product, this._productAttributeWineAging)
          || this._renderWineAttributes(product, this._productAttributeWineAlcohol)
          || this._renderWineAttributes(product, this._productAttributeWinePH)
          || this._renderWineAttributes(product, this._productAttributeWineTA)
          || this._renderWineAttributes(product, this._productAttributeWineF)) {
          return (
            <div className='product-attribute-nv'>
              <Heading
                headingTag='h4'
                text={filter}
                className='product-attribute-nv-name'
              />
              {this._renderWineAttributes(product, this._productAttributeWineBlend)}
              {this._renderWineAttributes(product, this._productAttributeWineAging)}
              {this._renderWineAttributes(product, this._productAttributeWineAlcohol, '%')}
              {this._renderWineAttributes(product, this._productAttributeWinePH)}
              {this._renderWineAttributes(product, this._productAttributeWineTA)}
              {this._renderWineAttributes(product, this._productAttributeWineF)}
            </div>
          );
        }
      } else {
        const productattribute = product.filter(
          attribute => attribute.Name!.toUpperCase() === filter.toUpperCase()
        );
        if (productattribute && productattribute.length && productattribute[0].TextValue) {
          const attributeHeading = (filter === this._productAttributeCookbookDescription ? this._productAttributeCookbookDescriptionHeader : productattribute[0].Name);
          return (
            <div className='product-attribute-nv'>
              <Heading
                headingTag='h4'
                text={attributeHeading || ''}
                className='product-attribute-nv-name'
              />
              <RichTextComponent
                className='product-attribute-nv-value'
                text={productattribute[0].TextValue}
              />
            </div>
          );
        }
      }
    }
    return null;
  }

  private _renderWineAttributes(
    product: AttributeValue[],
    filter: string,
    suffix?: string
  ): JSX.Element | null {
    const productattribute = product.filter(
      attribute => attribute.Name!.toLowerCase() === filter.toLowerCase()
    );
    if (productattribute && productattribute.length && productattribute[0].TextValue) {
      const attributeValue = this._renderProductCell(productattribute[0], suffix);
      const keyName = `${productattribute[0].Name}:`;
      if (attributeValue !== null) {
        return (
          <div className='product-attribute-wine'>
            <RichTextComponent
              className='product-attribute-wine-key'
              text={keyName}
            />
            <div className='product-attribute-wine-value'>{attributeValue}</div>
          </div>
        );
      }
    }
    return null;
  }

  private _renderProductCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    switch (product.DataTypeValue) {
      case 0: // none
        // Return span here instead of null so the row still shows
        return <span />;
      case 1: // Currency
        return this._renderCurrencyCell(product, suffix);
      case 2: // DateTime
        return this._renderDateTimeCell(product, suffix);
      case 3: // Decimal
        return this._renderDecimalCell(product, suffix);
      case 4: // Integer
        return this._renderIntegerCell(product, suffix);
      case 5: // Text
        return this._renderTextCell(product, suffix);
      case 6: // TrueFalse
        return this._renderTrueFalseCell(product);
      case 40: // Video
        this.props.telemetry.warning(
          'Got video specification type, not supported yet so skipping line'
        );
        return null;
      case 41: // Image
        this.props.telemetry.warning(
          'Got image specification type, not supported yet so skipping line'
        );
        return null;
      default:
        this.props.telemetry.warning(
          `Got unknown specification type ${product.DataTypeValue} so skipping line`
        );
        return null;
    }
  }

  private _renderCurrencyCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    if (product.CurrencyValue !== null && product.CurrencyValue !== undefined) {
      return (
        <span>
          {product.CurrencyCode}
          {product.CurrencyValue}
          {suffix}
        </span>
      );
    }

    return null;
  }

  private _renderDateTimeCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    if (
      product.DateTimeOffsetValue !== null &&
      product.DateTimeOffsetValue !== undefined &&
      product.DateTimeOffsetValue.toUTCString
    ) {
      return <span>{product.DateTimeOffsetValue.toUTCString()}{suffix}</span>;
    }

    return null;
  }

  private _renderIntegerCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    if (product.IntegerValue !== null && product.IntegerValue !== undefined) {
      return <span>{product.IntegerValue}{suffix}</span>;
    }

    return null;
  }

  private _renderDecimalCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    if (product.FloatValue !== null && product.FloatValue !== undefined) {
      return <span>{product.FloatValue}{suffix}</span>;
    }

    return null;
  }

  private _renderTextCell(product: AttributeValue, suffix?: string): JSX.Element | null {
    if (product.TextValue !== null && product.TextValue !== undefined) {
      return <RichTextComponent text={product.TextValue + (suffix || '')} />;
    }

    return null;
  }

  private _renderTrueFalseCell(product: AttributeValue): JSX.Element | null {
    if (product.BooleanValue) {
      return <span>{'Yes'}</span>;
    } else {
      return <span>{'No'}</span>;
    }
  }

  private _compareStrings(
    string1: string,
    string2: string,
    ignoreCase: boolean,
    useLocale: string
  ): boolean {
    if (ignoreCase) {
      if (useLocale) {
        string1 = string1.toLocaleLowerCase();
        string2 = string2.toLocaleLowerCase();
      } else {
        string1 = string1.toLowerCase();
        string2 = string2.toLowerCase();
      }
    }
    return string1 === string2;
  }
}

export default ProductAttributes;
